import type { MessageLanguages } from '@/i18n'

// Types pour les réponses API backend
interface Language {
  id: number
  code: string
  name: string
  isDefault: boolean
  isActive: boolean
}

interface TranslationKey {
  id: number
  keyPath: string
  description?: string
  context?: string
}

interface ApiResponse<T> {
  success: boolean
  data: T
  error?: string
}

// Configuration API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'

class TranslationService {
  private cache = new Map<string, unknown>()
  private cacheExpiry = new Map<string, number>()
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 heures (optimisé)

  /**
   * Vérifier si une entrée cache est valide
   */
  private isCacheValid(key: string): boolean {
    const expiry = this.cacheExpiry.get(key)
    return expiry ? Date.now() < expiry : false
  }

  /**
   * Mettre en cache une réponse
   */
  private setCache(key: string, data: unknown): void {
    this.cache.set(key, data)
    this.cacheExpiry.set(key, Date.now() + this.CACHE_DURATION)
  }

  /**
   * Récupérer les langues disponibles depuis le backend
   */
  async getLanguages(): Promise<Language[]> {
    const cacheKey = 'languages'

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey) as Language[]
    }

    try {
      const response = await fetch(`${API_BASE_URL}/languages`)
      const result: ApiResponse<Language[]> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de la récupération des langues')
      }

      this.setCache(cacheKey, result.data)
      return result.data
    } catch (error) {
      console.error('[TranslationService] Erreur récupération langues:', error)
      throw error
    }
  }

  /**
   * Récupérer toutes les traductions pour une langue
   */
  async getTranslationsForLanguage(
    languageCode: MessageLanguages
  ): Promise<Record<string, unknown>> {
    const cacheKey = `translations-${languageCode}`

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey) as Record<string, unknown>
    }

    try {
      const response = await fetch(`${API_BASE_URL}/translations/${languageCode}`)
      const result: ApiResponse<Record<string, string>> = await response.json()

      if (!result.success) {
        throw new Error(
          result.error || `Erreur lors de la récupération des traductions ${languageCode}`
        )
      }

      // Le backend retourne déjà un objet plat { "key": "value" }
      // On le convertit en objet hiérarchique pour Vue i18n
      const translationsObject = this.convertFlatToNestedObject(result.data)

      this.setCache(cacheKey, translationsObject)
      return translationsObject
    } catch (error) {
      console.error(`[TranslationService] Erreur récupération traductions ${languageCode}:`, error)
      throw error
    }
  }

  /**
   * Récupérer toutes les clés de traduction disponibles
   */
  async getTranslationKeys(): Promise<TranslationKey[]> {
    const cacheKey = 'translation-keys'

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey) as TranslationKey[]
    }

    try {
      const response = await fetch(`${API_BASE_URL}/translations/keys`)
      const result: ApiResponse<TranslationKey[]> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de la récupération des clés')
      }

      this.setCache(cacheKey, result.data)
      return result.data
    } catch (error) {
      console.error('[TranslationService] Erreur récupération clés:', error)
      throw error
    }
  }

  /**
   * Convertir un objet plat de traductions en objet hiérarchique
   * Ex: { "common.save": "Sauvegarder" } -> { common: { save: "Sauvegarder" } }
   */
  private convertFlatToNestedObject(
    flatTranslations: Record<string, string>
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {}

    Object.entries(flatTranslations).forEach(([keyPath, value]) => {
      const keys = keyPath.split('.')
      let current: any = result

      // Naviguer/créer la hiérarchie
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i]
        // Si le chemin n'existe pas, ou n'est pas un objet, on le crée/l'écrase.
        // C'est la correction clé pour éviter le TypeError.
        if (typeof current[key] !== 'object' || current[key] === null) {
          current[key] = {}
        }
        current = current[key]
      }

      // Assigner la valeur finale
      const finalKey = keys[keys.length - 1]
      // On n'écrase pas un objet (créé par une clé plus profonde) par une chaîne.
      // Cela évite la perte de traductions enfants.
      if (typeof current[finalKey] !== 'object' || current[finalKey] === null) {
        current[finalKey] = value
      }
    })

    return result
  }

  /**
   * Vider le cache (utile lors du changement de langue)
   */
  clearCache(): void {
    this.cache.clear()
    this.cacheExpiry.clear()
  }

  /**
   * Vider le cache pour une langue spécifique
   */
  clearLanguageCache(languageCode: MessageLanguages): void {
    const cacheKey = `translations-${languageCode}`
    this.cache.delete(cacheKey)
    this.cacheExpiry.delete(cacheKey)
  }
}

// Export singleton
export const translationService = new TranslationService()
export default translationService
